import { Filter, FilterX, X } from "lucide-react";
import { Filters } from "../../types";
import { useAuth } from "../../context/AuthContext";

interface PatientFiltersProps {
  setFilterDetails: (filters: any) => void;
  filterDetails: Filters;
}

export function PatientFilters({ setFilterDetails, filterDetails }: PatientFiltersProps) {
  const { user } = useAuth();

  const onFilterButtonClick = (success: boolean) => {
    setFilterDetails({
      ...filterDetails,
      success: success,
    });
  };

  const clearFilters = () => {
    setFilterDetails({
      ...filterDetails,
      gender: "",
      status: "",
    });
  };

  const hasActiveFilters = filterDetails.gender || filterDetails.status;

  // Get positioning classes based on user role
  const getPositionClasses = () => {
    const role = user?.role?.toLowerCase();
    switch (role) {
      case "admin":
        return "relative -top-8";
      case "clinic":
      case "medicalcamp":
        return "relative -top-1";
      default:
        return "";
    }
  };

  const getDropdownPosition = () => {
    const role = user?.role?.toLowerCase();
    switch (role) {
      case "admin":
        return "absolute top-16 left-0";
      case "clinic":
      case "medicalcamp":
        return "absolute top-16 right-0";
      default:
        return "absolute top-16 left-0";
    }
  };

  return (
    <div className={`flex gap-4 ${getPositionClasses()}`}>
      {/* Filter Toggle Button */}
      <div className="relative">
        <button
          onClick={() => onFilterButtonClick(!filterDetails?.success)}
          className={`
            relative rounded-lg border-2 p-3 transition-all duration-200 
            ${
              filterDetails?.success
                ? "bg-blue-50 border-blue-500 text-blue-600 hover:bg-blue-100"
                : "bg-white border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600"
            }
            ${hasActiveFilters ? "shadow-sm" : ""}
          `}
          aria-label={filterDetails?.success ? "Close filters" : "Open filters"}
        >
          {filterDetails?.success ? (
            <FilterX className="w-5 h-5" />
          ) : (
            <Filter className="w-5 h-5" />
          )}

          {/* Active filters indicator */}
          {hasActiveFilters && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
          )}
        </button>

        {/* Filter Dropdown */}
        {filterDetails?.success && (
          <div
            className={`
            ${getDropdownPosition()} z-20 mt-2 w-80 bg-white rounded-xl 
            border border-gray-200 shadow-xl p-6 animate-in fade-in-0 zoom-in-95 duration-200
          `}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => onFilterButtonClick(false)}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close filters"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Gender Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  value={filterDetails.gender}
                  onChange={(e) =>
                    setFilterDetails({
                      ...filterDetails,
                      gender: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                    bg-white hover:border-gray-400 transition-colors"
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              {/* Patient Availability Selection */}
              {user?.role !== "admin" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Availability
                  </label>
                  <select
                    value={filterDetails.status}
                    onChange={(e) =>
                      setFilterDetails({
                        ...filterDetails,
                        status: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm 
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                      bg-white hover:border-gray-400 transition-colors"
                  >
                    <option value="">All Patients</option>
                    <option value="Booked">Upcoming Appointments</option>
                    <option value="Completed">Treated Patients</option>
                  </select>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 
                  border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none 
                  focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50 
                  disabled:cursor-not-allowed"
              >
                Clear Filters
              </button>
              <button
                onClick={() => onFilterButtonClick(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 
                  rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 
                  focus:ring-blue-500 transition-colors"
              >
                Apply Filters
              </button>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {filterDetails.gender && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 
                      text-blue-800 text-xs font-medium rounded-full"
                    >
                      Gender: {filterDetails.gender}
                      <button
                        onClick={() => setFilterDetails({ ...filterDetails, gender: "" })}
                        className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {filterDetails.status && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 
                      text-blue-800 text-xs font-medium rounded-full"
                    >
                      Status: {filterDetails.status === "Booked" ? "Upcoming" : "Treated"}
                      <button
                        onClick={() => setFilterDetails({ ...filterDetails, status: "" })}
                        className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
